import { useState } from "react";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Field, useToast, type FieldDef } from "./admin-ui";

/**
 * A single-record settings form. Used for Social Media, Website Settings and
 * SEO — modules that manage one object rather than a collection.
 */
export function SettingsPage<T extends { id: string }>({
  title,
  description,
  storageKey,
  seed,
  fields,
  successMessage = "Settings saved successfully.",
}: {
  title: string;
  description: string;
  storageKey: string;
  seed: T;
  fields: FieldDef[];
  successMessage?: string;
}) {
  const { items, update } = useCollection<T>(storageKey, [seed]);
  const { show } = useToast();
  const record = items[0] ?? seed;

  const [form, setForm] = useState<Record<string, unknown>>({ ...record });
  const [dirty, setDirty] = useState(false);

  function save() {
    update(record.id, form as Partial<T>);
    setDirty(false);
    show(successMessage);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        subtitle={description}
        actions={
          <button
            type="button"
            onClick={save}
            disabled={!dirty}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line text-sm" />
            </span>
            Save Changes
          </button>
        }
      />

      <div className="bg-white rounded-lg border border-background-200 p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <Field
              key={f.name}
              def={f}
              value={form[f.name]}
              onChange={(v) => {
                setForm((prev) => ({ ...prev, [f.name]: v }));
                setDirty(true);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}